import React, { useState, useEffect, useCallback, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

// ─── Stellar Explorer helpers ─────────────────────────────────────────────────
// Priority: explicit REACT_APP_STELLAR_NETWORK → parse RPC URL → default testnet
// Set REACT_APP_STELLAR_NETWORK=testnet (or mainnet) in Azure App Service
// Application Settings so the build picks it up correctly.
const STELLAR_NETWORK: 'testnet' | 'mainnet' = (() => {
  const explicit = process.env.REACT_APP_STELLAR_NETWORK;
  if (explicit === 'mainnet') return 'mainnet';
  if (explicit === 'testnet') return 'testnet';
  // Fallback: parse RPC URL
  const rpc = process.env.REACT_APP_SOROBAN_RPC_URL || '';
  return rpc.includes('testnet') ? 'testnet' : 'testnet'; // default testnet
})();

const HORIZON_BASE =
  STELLAR_NETWORK === 'mainnet'
    ? 'https://horizon.stellar.org'
    : 'https://horizon-testnet.stellar.org';

function explorerContract(id?: string | null) {
  if (!id) return null;
  return `https://stellar.expert/explorer/${STELLAR_NETWORK}/contract/${id}`;
}
function explorerTx(hash?: string | null) {
  if (!hash) return null;
  return `https://stellar.expert/explorer/${STELLAR_NETWORK}/tx/${hash}`;
}

const REWARDS_CONTRACT_ID = process.env.REACT_APP_GEOFENCE_REWARDS_ID || null;
const CORE_CONTRACT_ID    = process.env.REACT_APP_V2_CORE_ID || null;
const MAPBOX_TOKEN        = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Well-known Soroban token contract addresses
// Override with REACT_APP_USDC_TOKEN_ID / REACT_APP_XLM_TOKEN_ID env vars
const TOKEN_PRESETS: Array<{ label: string; symbol: string; addr: string; decimals: number; unit: string }> = (() => {
  const net = STELLAR_NETWORK;
  const usdcAddr = process.env.REACT_APP_USDC_TOKEN_ID || (
    net === 'mainnet'
      ? 'CCW67TSZV3SSS2HXMBQ5JFGCKJNXKZM7UQUWUZPUTHXSTZLEO7SJMI'
      : 'CBIELTK6YBZJU5UP2WWQEUCYKLPU6AUNZ2BQ4WWFEIE3USCIHMXQDAMA'
  );
  const xlmAddr = process.env.REACT_APP_XLM_TOKEN_ID || (
    net === 'mainnet'
      ? 'CAS3J7GYLGXMF6TDJBBYYSE3HQ6BBSMLNUQ34T6TZMYMW2EVH34XOWMA'
      : 'CDMLFMKMMD7MWZP3FKUBZPVHTUEDLSX4BYGYKH4GCESXYHS3IHQ4EIG'
  );
  return [
    { label: 'USDC', symbol: 'USDC', addr: usdcAddr, decimals: 7, unit: '1 USDC = 10,000,000' },
    { label: 'XLM',  symbol: 'XLM',  addr: xlmAddr,  decimals: 7, unit: '1 XLM = 10,000,000 stroops' },
  ];
})();

// ─── Responsive hook ──────────────────────────────────────────────────────────
function useIsMobile(breakpoint = 680) {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface Proposal {
  id: string;
  countryCode: string;
  status: 'submitted' | 'approved' | 'rejected' | string;
  submittedBy?: string;
  createdAt: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  rewardAmount?: number;
  rewardCurrency?: string;
  rewardTxHash?: string;
  originalFeatureSnapshot?: any;
  proposedFeatureSnapshot?: any;
  onChainAnchoredAt?: string;
  onChainSubmitHash?: string;
  onChainApproveHash?: string;
  onChainFundHash?: string;
  onChainReleaseHash?: string;
  adminNote?: string;
  paymentInfo?: any;
}

interface ProposalDiff {
  vertices?: { original: number; proposed: number; delta: number; deltaPct?: number | null; changedPositions?: number | null };
  area?: { approxOriginalKm2: number; approxProposedKm2: number; deltaPct: number | null };
  centroid?: { approxShiftKm: number };
  geometryTypeChanged?: boolean;
}

interface BorderProposalsDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  walletAddress: string | null;
  mainAdminAddress: string | null;
  isAdmin: boolean;
  /** Classic wallet XLM balance display string from App (Horizon) */
  accountBalance?: string | null;
  /** Linked passkey smart-account `C…` address, if any */
  passkeyAccountAddress?: string | null;
  /** Bump Horizon + passkey balance reads (e.g. from App) */
  onRefreshWalletBalances?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  submitted: { bg: '#fef3c7', color: '#92400e', label: 'Submitted' },
  approved:  { bg: '#d1fae5', color: '#065f46', label: 'Approved'  },
  rejected:  { bg: '#fee2e2', color: '#991b1b', label: 'Rejected'  },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_COLORS[status] || { bg: '#f3f4f6', color: '#374151', label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '9999px',
      fontSize: '11px', fontWeight: 700, backgroundColor: s.bg, color: s.color,
      textTransform: 'uppercase' as const, letterSpacing: '0.04em',
    }}>
      {s.label}
    </span>
  );
}

function fmtDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function truncAddr(addr?: string | null) {
  if (!addr) return '—';
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function submitterCountKey(submittedBy?: string | null) {
  const t = submittedBy != null ? String(submittedBy).trim() : '';
  return t || '__none__';
}

/** Horizon transaction record (subset of fields we render). */
interface HorizonTxRecord {
  id?: string;
  hash?: string;
  created_at?: string;
  successful?: boolean;
  ledger?: number;
  fee_charged?: string;
  operation_count?: number;
  source_account?: string;
  memo_type?: string;
  memo?: string;
}

const TX_PAGE_LIMIT = 15;

const STROOPS_PER_XLM = BigInt(10_000_000);

function stroopsToXlm(stroops?: string | null): string {
  if (stroops == null || stroops === '') return '—';
  const n = BigInt(stroops);
  const whole = n / STROOPS_PER_XLM;
  const frac = n % STROOPS_PER_XLM;
  if (frac === BigInt(0)) return `${whole}`;
  const fracStr = String(frac).padStart(7, '0').replace(/0+$/, '');
  return `${whole}.${fracStr}`;
}

function formatHorizonOperation(op: any, viewerG: string): string {
  if (!op?.type) return 'Operation';
  const t = op.type;
  if (t === 'payment') {
    const asset = op.asset_type === 'native' ? 'XLM' : `${op.asset_code || '?'}`;
    const amt = op.amount ?? '?';
    if (op.from === viewerG) return `Send ${amt} ${asset} → ${truncAddr(op.to)}`;
    if (op.to === viewerG) return `Receive ${amt} ${asset} ← ${truncAddr(op.from)}`;
    return `Payment ${amt} ${asset} (${truncAddr(op.from)} → ${truncAddr(op.to)})`;
  }
  if (t === 'create_account') {
    return `Create ${truncAddr(op.account)} · start ${op.starting_balance ?? '?'} XLM`;
  }
  if (t === 'change_trust') {
    const asset = op.asset_type === 'native' ? 'XLM' : `${op.asset_code || '?'}`;
    return `Trustline ${asset} · limit ${op.limit ?? '—'}`;
  }
  if (t === 'manage_sell_offer' || t === 'manage_buy_offer') {
    return `${t.includes('sell') ? 'Sell' : 'Buy'} offer · ${op.amount ?? '?'} ${op.selling_asset_type === 'native' ? 'XLM' : op.selling_asset_code || 'asset'}`;
  }
  if (t === 'path_payment_strict_send' || t === 'path_payment_strict_receive') {
    return `Path payment · ${op.amount ?? '?'} ${op.asset_type === 'native' ? 'XLM' : ''}`;
  }
  if (t === 'invoke_host_function') return 'Soroban contract invocation';
  if (t === 'extend_footprint_ttl') return 'Extend Soroban TTL';
  if (t === 'bump_sequence') return 'Bump sequence';
  if (t === 'account_merge') return `Merge into ${truncAddr(op.into)}`;
  if (t === 'set_options') return 'Account options updated';
  if (t === 'begin_sponsoring_future_reserves' || t === 'end_sponsoring_future_reserves') return t.replace(/_/g, ' ');
  if (t === 'claim_claimable_balance') return 'Claim claimable balance';
  if (t === 'create_claimable_balance') return 'Create claimable balance';
  return String(t).replace(/_/g, ' ');
}

function formatMemo(tx: HorizonTxRecord): string | null {
  const mt = tx.memo_type;
  if (!mt || mt === 'none') return null;
  const m = tx.memo;
  if (m == null || m === '') return `Memo (${mt})`;
  return `Memo (${mt}): ${String(m).length > 80 ? `${String(m).slice(0, 77)}…` : m}`;
}

function countGeomVertices(g: any): number {
  if (!g?.coordinates) return 0;
  if (g.type === 'Polygon') return (g.coordinates as number[][][]).flatMap(r => r).length;
  if (g.type === 'MultiPolygon') return (g.coordinates as number[][][][]).flatMap(poly => poly.flatMap(r => r)).length;
  return 0;
}

function vertexDelta(p: Proposal): string {
  const orig = p.originalFeatureSnapshot?.geometry;
  const prop = p.proposedFeatureSnapshot?.geometry;
  if (!orig || !prop) return '—';
  const delta = countGeomVertices(prop) - countGeomVertices(orig);
  return delta > 0 ? `+${delta}` : String(delta);
}

// ─── Tx / Contract link atoms ─────────────────────────────────────────────────

function TxLink({ hash }: { hash?: string | null }) {
  const url = explorerTx(hash);
  if (!hash || !url) return <span style={{ color: '#94a3b8' }}>—</span>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={hash}
      style={{ color: '#0369a1', fontFamily: 'monospace', fontSize: '11px', textDecoration: 'none', whiteSpace: 'nowrap' as const }}>
      {truncAddr(hash)} ↗
    </a>
  );
}

function ContractLink({ id, label }: { id?: string | null; label: string }) {
  const url = explorerContract(id);
  if (!id || !url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={id}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#0369a1', fontSize: '11px', textDecoration: 'none' }}>
      {label} ↗
    </a>
  );
}

// ─── Contract links bar (header) ──────────────────────────────────────────────

function ContractLinksBar() {
  if (!REWARDS_CONTRACT_ID && !CORE_CONTRACT_ID) return null;
  const pill: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '3px 10px', borderRadius: '9999px',
    fontSize: '11px', fontWeight: 600, textDecoration: 'none',
    backgroundColor: 'rgba(255,255,255,0.12)', color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.18)',
  };
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const, marginTop: '8px' }}>
      {REWARDS_CONTRACT_ID && (
        <a href={explorerContract(REWARDS_CONTRACT_ID)!} target="_blank" rel="noopener noreferrer" style={pill}>
          ◆ Rewards Contract ({STELLAR_NETWORK}) ↗
        </a>
      )}
      {CORE_CONTRACT_ID && (
        <a href={explorerContract(CORE_CONTRACT_ID)!} target="_blank" rel="noopener noreferrer" style={pill}>
          ⬡ Core Contract ({STELLAR_NETWORK}) ↗
        </a>
      )}
    </div>
  );
}

// ─── Horizon transaction row (full detail, optional explorer link) ─────────────

function TransactionHistoryCard({
  tx,
  viewerG,
  opLines,
  isMobile,
  onCopyHash,
  copiedHash,
}: {
  tx: HorizonTxRecord;
  viewerG: string;
  opLines: string[];
  isMobile: boolean;
  onCopyHash: (h: string) => void;
  copiedHash: string | null;
}) {
  const hash = tx.hash || '';
  const expUrl = explorerTx(hash);
  const memoLine = formatMemo(tx);
  const sourceNote =
    tx.source_account && tx.source_account !== viewerG
      ? `Fee/source account: ${tx.source_account}`
      : null;

  return (
    <article
      style={{
        marginBottom: isMobile ? 14 : 12,
        padding: isMobile ? '12px 12px' : '14px 16px',
        borderRadius: 10,
        border: '1px solid #e2e8f0',
        backgroundColor: '#f8fafc',
        fontSize: 12,
        color: '#334155',
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap' as const, alignItems: 'center', gap: '8px 12px', marginBottom: 8 }}>
        {tx.created_at && (
          <span style={{ fontWeight: 700, color: '#0f172a' }}>{fmtDate(tx.created_at)}</span>
        )}
        {tx.ledger != null && (
          <span style={{ color: '#64748b' }}>Ledger <strong>{tx.ledger}</strong></span>
        )}
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase' as const,
            padding: '2px 8px',
            borderRadius: 9999,
            backgroundColor: tx.successful === false ? '#fee2e2' : '#d1fae5',
            color: tx.successful === false ? '#991b1b' : '#065f46',
          }}
        >
          {tx.successful === false ? 'Failed' : 'Success'}
        </span>
        <span style={{ color: '#64748b' }}>
          Fee <strong>{stroopsToXlm(tx.fee_charged)}</strong> XLM
        </span>
        {tx.operation_count != null && (
          <span style={{ color: '#64748b' }}>
            {tx.operation_count} op{tx.operation_count !== 1 ? 's' : ''}.
          </span>
        )}
      </div>

      {hash ? (
        <div style={{ marginBottom: 8, display: 'flex', flexDirection: isMobile ? 'column' as const : 'row' as const, alignItems: isMobile ? 'stretch' : 'center', gap: 8 }}>
          <code
            style={{
              flex: 1,
              minWidth: 0,
              display: 'block',
              fontSize: 11,
              lineHeight: 1.45,
              wordBreak: 'break-all' as const,
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              padding: '8px 10px',
              color: '#0f172a',
            }}
          >
            {hash}
          </code>
          <button
            type="button"
            onClick={() => onCopyHash(hash)}
            style={{
              minHeight: 40,
              padding: '8px 14px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              backgroundColor: '#fff',
              cursor: 'pointer',
              color: '#374151',
              flexShrink: 0,
            }}
          >
            {copiedHash === hash ? 'Copied' : 'Copy hash'}
          </button>
        </div>
      ) : null}

      {memoLine && (
        <div style={{ marginBottom: 6, fontSize: 11, color: '#475569', wordBreak: 'break-word' as const }}>{memoLine}</div>
      )}
      {sourceNote && (
        <div style={{ marginBottom: 6, fontSize: 11, color: '#64748b', wordBreak: 'break-all' as const }}>{sourceNote}</div>
      )}

      {opLines.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 6 }}>
            Operations
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#475569', lineHeight: 1.5 }}>
            {opLines.map((line, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {expUrl && (
        <div style={{ marginTop: 10, fontSize: 11 }}>
          <a href={expUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8' }}>
            Open in Stellar Expert ↗
          </a>
        </div>
      )}
    </article>
  );
}

// ─── Bbox helper (used by MapboxBorderMap) ────────────────────────────────────

function computeBbox(features: any[]): [number, number, number, number] {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  features.forEach(feat => {
    const geom = feat?.geometry;
    if (!geom?.coordinates) return;
    const rings: number[][][] = [];
    if (geom.type === 'Polygon') geom.coordinates.forEach((r: number[][]) => rings.push(r));
    else if (geom.type === 'MultiPolygon') geom.coordinates.forEach((p: number[][][]) => p.forEach((r: number[][]) => rings.push(r)));
    rings.forEach(ring => ring.forEach(([lng, lat]) => {
      if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
    }));
  });
  const pl = (maxLng - minLng) * 0.07 || 0.1;
  const pb = (maxLat - minLat) * 0.07 || 0.1;
  return [minLng - pl, minLat - pb, maxLng + pl, maxLat + pb];
}

// ─── Mapbox globe map for border comparison ───────────────────────────────────

interface MapboxBorderMapProps {
  /** Features to render, each with its own highlight colour */
  features: Array<{ feature: any; color: string }>;
  width: number;
  height: number;
  /** [minLng, minLat, maxLng, maxLat] */
  bbox: [number, number, number, number];
}

function MapboxBorderMap({ features, width, height, bbox }: MapboxBorderMapProps) {
  const containerRef  = useRef<HTMLDivElement>(null);
  const mapRef        = useRef<mapboxgl.Map | null>(null);
  const mountedRef    = useRef(false);

  // Initialise once per mount
  useEffect(() => {
    if (!containerRef.current || mountedRef.current) return;
    mountedRef.current = true;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const m = new mapboxgl.Map({
      container:        containerRef.current,
      style:            'mapbox://styles/mapbox/light-v11',
      projection:       'globe' as any,
      bounds:           [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
      fitBoundsOptions: { padding: 40, maxZoom: 10 },
      attributionControl: false,
      interactive:      true,
    });

    m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    m.once('load', () => {
      // No setFog — no atmosphere/stars, matches the main app globe
      features.forEach(({ feature, color }, idx) => {
        if (!feature?.geometry?.coordinates) return;
        const sid = `compare-${idx}`;
        m.addSource(sid, { type: 'geojson', data: feature });
        m.addLayer({ id: `${sid}-fill`, type: 'fill',   source: sid, paint: { 'fill-color': color, 'fill-opacity': 0.35 } });
        m.addLayer({ id: `${sid}-line`, type: 'line',   source: sid, paint: { 'line-color': color, 'line-width': 2.5, 'line-opacity': 0.9 } });
      });
    });

    mapRef.current = m;
    return () => {
      m.remove();
      mapRef.current  = null;
      mountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resize whenever the container dimensions change (e.g., fullscreen toggle)
  useEffect(() => {
    if (!mapRef.current) return;
    const m = mapRef.current;
    const t = setTimeout(() => {
      m.resize();
      m.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 40, maxZoom: 10 });
    }, 60);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  return (
    <div ref={containerRef} style={{ width, height, borderRadius: 8, overflow: 'hidden', position: 'relative' as const }} />
  );
}

// ─── Border Compare Modal ─────────────────────────────────────────────────────

interface BorderCompareModalProps {
  proposal: Proposal;
  onClose: () => void;
}

function BorderCompareModal({ proposal, onClose }: BorderCompareModalProps) {
  const isMobile = useIsMobile(700);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const orig = proposal.originalFeatureSnapshot;
  const prop = proposal.proposedFeatureSnapshot;

  const hasOrig = !!(orig?.geometry?.coordinates?.length);
  const hasProp = !!(prop?.geometry?.coordinates?.length);

  const validFeatures = [orig, prop].filter(f => f?.geometry?.coordinates?.length);
  const bbox: [number, number, number, number] = validFeatures.length
    ? computeBbox(validFeatures)
    : [-180, -90, 180, 90];

  // Compute SVG dimensions that adapt to normal vs fullscreen
  const SVG_W = (() => {
    if (isFullscreen) {
      const avail = window.innerWidth - (isMobile ? 32 : 48);
      if (showOverlay || isMobile) return Math.min(avail, 1000);
      return Math.floor((avail - 24) / 2);
    }
    return isMobile ? Math.min(window.innerWidth - 48, 340) : 380;
  })();
  const SVG_H = Math.round(SVG_W * (isFullscreen ? 0.65 : 0.6));

  const hasOnChain = !!(proposal.onChainSubmitHash || proposal.onChainApproveHash ||
    proposal.onChainFundHash || proposal.onChainReleaseHash);

  const btnStyle: React.CSSProperties = {
    background: 'none', border: '1px solid #334155', color: '#94a3b8',
    borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
    padding: '4px 10px', lineHeight: 1, whiteSpace: 'nowrap',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 11000,
        backgroundColor: isFullscreen ? 'rgba(0,0,0,0.95)' : 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: isFullscreen ? 'stretch' : (isMobile ? 'flex-end' : 'center'),
        justifyContent: isFullscreen ? 'stretch' : 'center',
        padding: isFullscreen ? 0 : (isMobile ? 0 : '16px'),
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        backgroundColor: '#0f172a',
        borderRadius: isFullscreen ? 0 : (isMobile ? '16px 16px 0 0' : '14px'),
        width: isFullscreen ? '100vw' : (isMobile ? '100%' : 'min(900px, 96vw)'),
        height: isFullscreen ? '100vh' : undefined,
        maxHeight: isFullscreen ? '100vh' : (isMobile ? '92vh' : '90vh'),
        overflowY: 'auto',
        boxShadow: isFullscreen ? 'none' : '0 24px 80px rgba(0,0,0,0.6)',
        color: '#e2e8f0',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #1e293b',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky' as const, top: 0, backgroundColor: '#0f172a', zIndex: 1,
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' as const }}>
              Border Comparison
              <span style={{ backgroundColor: '#1e40af', color: '#bfdbfe', padding: '2px 10px', borderRadius: '9999px', fontSize: '12px' }}>
                {proposal.countryCode}
              </span>
              <span style={{ fontSize: '11px', color: '#475569', fontWeight: 400 }}>{proposal.id.slice(0, 8)}…</span>
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{fmtDate(proposal.createdAt)}</div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#94a3b8', cursor: 'pointer', whiteSpace: 'nowrap' as const }}>
              <input type="checkbox" checked={showOverlay} onChange={e => setShowOverlay(e.target.checked)} style={{ accentColor: '#60a5fa' }} />
              Overlay
            </label>
            <button onClick={() => setIsFullscreen(f => !f)} style={btnStyle} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              {isFullscreen ? '⊠ Exit' : '⛶ Expand'}
            </button>
            <button onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '22px', cursor: 'pointer', lineHeight: 1, padding: '4px' }}>
              ✕
            </button>
          </div>
        </div>

        <div style={{ padding: isMobile ? '16px' : '20px 24px', flex: 1, overflowY: 'auto' }}>
          {/* Legend */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '14px', fontSize: '12px', color: '#94a3b8', flexWrap: 'wrap' as const }}>
            <span><span style={{ color: '#f97316', fontWeight: 700 }}>■</span> Current border</span>
            <span><span style={{ color: '#22c55e', fontWeight: 700 }}>■</span> Proposed border</span>
            {showOverlay && <span style={{ color: '#60a5fa', fontSize: '11px' }}>Both borders rendered on the same globe — overlap shows where they differ</span>}
          </div>

          {showOverlay ? (
            /* ── Overlay: single globe map, both borders rendered on it ── */
            <div style={{ display: 'flex', justifyContent: 'center' as const }}>
              {(hasOrig || hasProp) ? (
                <MapboxBorderMap
                  key="overlay"
                  features={[
                    ...(hasOrig ? [{ feature: orig, color: '#f97316' }] : []),
                    ...(hasProp ? [{ feature: prop, color: '#22c55e' }] : []),
                  ]}
                  width={SVG_W}
                  height={SVG_H}
                  bbox={bbox}
                />
              ) : (
                <div style={{ width: SVG_W, height: SVG_H, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1e293b', borderRadius: 8, fontSize: 13, color: '#64748b' }}>
                  No geometry data
                </div>
              )}
            </div>
          ) : (
            /* ── Side-by-side: two independent globe maps ── */
            <div style={{
              display: 'grid',
              gridTemplateColumns: (isMobile && !isFullscreen) ? '1fr' : '1fr 1fr',
              gap: isMobile ? '16px' : '24px',
            }}>
              <div>
                <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' as const }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f97316', display: 'inline-block' }} />
                  <span style={{ fontWeight: 700, fontSize: '13px', color: '#f1f5f9' }}>Current Border</span>
                  {orig?.geometry?.type && (
                    <span style={{ fontSize: '10px', color: '#64748b', backgroundColor: '#1e293b', padding: '1px 6px', borderRadius: '4px' }}>
                      {orig.geometry.type}
                    </span>
                  )}
                </div>
                {hasOrig
                  ? <MapboxBorderMap key="original" features={[{ feature: orig, color: '#f97316' }]} width={SVG_W} height={SVG_H} bbox={bbox} />
                  : <div style={{ width: SVG_W, height: SVG_H, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1e293b', borderRadius: 8, fontSize: 12, color: '#64748b' }}>No snapshot</div>
                }
              </div>
              <div>
                <div style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' as const }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block' }} />
                  <span style={{ fontWeight: 700, fontSize: '13px', color: '#f1f5f9' }}>Proposed Border</span>
                  {prop?.geometry?.type && (
                    <span style={{ fontSize: '10px', color: '#64748b', backgroundColor: '#1e293b', padding: '1px 6px', borderRadius: '4px' }}>
                      {prop.geometry.type}
                    </span>
                  )}
                </div>
                {hasProp
                  ? <MapboxBorderMap key="proposed" features={[{ feature: prop, color: '#22c55e' }]} width={SVG_W} height={SVG_H} bbox={bbox} />
                  : <div style={{ width: SVG_W, height: SVG_H, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1e293b', borderRadius: 8, fontSize: 12, color: '#64748b' }}>No snapshot</div>
                }
              </div>
            </div>
          )}

          {/* On-chain tx links */}
          {hasOnChain && (
            <div style={{ marginTop: '20px', padding: '12px 14px', backgroundColor: '#0a1929', borderRadius: '8px', border: '1px solid #1e3a5f' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#60a5fa', marginBottom: '8px', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                On-Chain Transactions · <span style={{ color: '#475569', fontWeight: 400, textTransform: 'none' as const }}>{STELLAR_NETWORK}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '8px 20px', fontSize: '12px', color: '#94a3b8' }}>
                {proposal.onChainSubmitHash  && <div>Submit:  <TxLink hash={proposal.onChainSubmitHash} /></div>}
                {proposal.onChainApproveHash && <div>Approve: <TxLink hash={proposal.onChainApproveHash} /></div>}
                {proposal.onChainFundHash    && <div>Fund:    <TxLink hash={proposal.onChainFundHash} /></div>}
                {proposal.onChainReleaseHash && <div>Release: <TxLink hash={proposal.onChainReleaseHash} /></div>}
              </div>
              {REWARDS_CONTRACT_ID && (
                <div style={{ marginTop: '8px', fontSize: '11px', color: '#475569' }}>
                  Contract: <ContractLink id={REWARDS_CONTRACT_ID} label={`${REWARDS_CONTRACT_ID.slice(0, 8)}…${REWARDS_CONTRACT_ID.slice(-4)}`} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Row detail panel ─────────────────────────────────────────────────────────

function ProposalDetailPanel({ proposal, walletAddress, onActionDone, onCompare }: {
  proposal: Proposal;
  walletAddress: string | null;
  /** After approve / reject / on-chain / delete — collapse row, close compare, refresh list */
  onActionDone: () => void;
  onCompare: () => void;
}) {
  const [diff, setDiff] = useState<ProposalDiff | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting]  = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // On-chain rewards state
  const [onChainReceipt, setOnChainReceipt] = useState<any>(null);
  const [onChainEscrow, setOnChainEscrow]   = useState<any>(null);
  const [onChainChecked, setOnChainChecked] = useState(false);
  const [isAnchoring, setIsAnchoring]       = useState(false);
  const [isFunding, setIsFunding]           = useState(false);
  const [isReleasing, setIsReleasing]       = useState(false);
  const [isRefunding, setIsRefunding]       = useState(false);
  const [isDeleting, setIsDeleting]         = useState(false);
  const [fundTokenAddr, setFundTokenAddr]   = useState('');
  const [fundAmountStr, setFundAmountStr]   = useState('');

  const isApprovedOrBeyond = ['approved', 'rewardFunded', 'paid'].includes(proposal.status);

  useEffect(() => {
    if (!walletAddress) return;
    fetch(`/api/admin/geofence/proposals/${proposal.id}/diff?walletAddress=${encodeURIComponent(walletAddress)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.diff) setDiff(d.diff); })
      .catch(() => {});
  }, [proposal.id, walletAddress]);

  // Fetch on-chain status for approved+ proposals
  const fetchOnChain = useCallback(() => {
    if (!walletAddress || !isApprovedOrBeyond) return;
    fetch(`/api/admin/geofence/proposals/${proposal.id}/onchain?walletAddress=${encodeURIComponent(walletAddress)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        setOnChainChecked(true);
        if (d?.success) {
          setOnChainReceipt(d.receipt ?? null);
          setOnChainEscrow(d.escrow ?? null);
        }
      })
      .catch(() => setOnChainChecked(true));
  }, [proposal.id, walletAddress, isApprovedOrBeyond]);

  useEffect(() => { fetchOnChain(); }, [fetchOnChain]);

  const adminPost = useCallback(async (action: string, body: Record<string, any> = {}) => {
    const res = await fetch(`/api/admin/geofence/proposals/${proposal.id}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ walletAddress: walletAddress ?? undefined, ...body }),
    });
    return res.json();
  }, [proposal.id, walletAddress]);

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      const data = await adminPost('approve');
      if (data.success) { setNotification({ type: 'success', msg: 'Approved. Border is now live.' }); onActionDone(); }
      else {
        const forced = await adminPost('approve', { force: true });
        if (forced.success) { setNotification({ type: 'success', msg: 'Approved (forced).' }); onActionDone(); }
        else setNotification({ type: 'error', msg: forced.error || 'Approval failed' });
      }
    } catch (e: any) { setNotification({ type: 'error', msg: e.message }); }
    finally { setIsApproving(false); }
  };

  const handleReject = async () => {
    const reason = window.prompt('Rejection reason (optional):') ?? '';
    setIsRejecting(true);
    try {
      const data = await adminPost('reject', { reason });
      if (data.success) { setNotification({ type: 'success', msg: 'Rejected.' }); onActionDone(); }
      else setNotification({ type: 'error', msg: data.error || 'Rejection failed' });
    } catch (e: any) { setNotification({ type: 'error', msg: e.message }); }
    finally { setIsRejecting(false); }
  };

  const handleAnchor = async () => {
    setIsAnchoring(true);
    try {
      const data = await adminPost('anchor');
      if (!data.success) throw new Error(data.error || 'Anchor failed');
      setNotification({ type: 'success', msg: `Anchored on-chain. tx: ${(data.submitHash || '').slice(0, 10)}…` });
      fetchOnChain();
      onActionDone();
    } catch (e: any) { setNotification({ type: 'error', msg: e.message }); }
    finally { setIsAnchoring(false); }
  };

  const handleFund = async () => {
    if (!fundTokenAddr.trim() || !fundAmountStr.trim()) return;
    setIsFunding(true);
    try {
      const data = await adminPost('fund-onchain', { tokenAddr: fundTokenAddr.trim(), amount: parseInt(fundAmountStr, 10) });
      if (!data.success) throw new Error(data.error || 'Fund failed');
      setNotification({ type: 'success', msg: `Escrow funded. tx: ${(data.hash || '').slice(0, 10)}…` });
      fetchOnChain(); onActionDone();
    } catch (e: any) { setNotification({ type: 'error', msg: e.message }); }
    finally { setIsFunding(false); }
  };

  const handleRelease = async () => {
    setIsReleasing(true);
    try {
      const data = await adminPost('release-onchain');
      if (!data.success) throw new Error(data.error || 'Release failed');
      setNotification({ type: 'success', msg: `Reward released. tx: ${(data.hash || '').slice(0, 10)}…` });
      fetchOnChain(); onActionDone();
    } catch (e: any) { setNotification({ type: 'error', msg: e.message }); }
    finally { setIsReleasing(false); }
  };

  const handleRefund = async () => {
    if (!window.confirm('Refund the escrowed reward back to the treasury? This cannot be undone.')) return;
    setIsRefunding(true);
    try {
      const data = await adminPost('refund-onchain');
      if (!data.success) throw new Error(data.error || 'Refund failed');
      setNotification({ type: 'success', msg: `Escrow refunded to treasury. tx: ${(data.hash || '').slice(0, 10)}…` });
      fetchOnChain(); onActionDone();
    } catch (e: any) { setNotification({ type: 'error', msg: e.message }); }
    finally { setIsRefunding(false); }
  };

  const handleDelete = async () => {
    const isApproved = proposal.status === 'approved';
    const warning = isApproved
      ? `Delete this approved proposal?\n\nThe border change is already LIVE in countries.geojson — deleting this record does NOT revert it. Only the proposal history entry will be removed.\n\nType DELETE to confirm:`
      : `Permanently delete this ${proposal.status} proposal? This cannot be undone.\n\nType DELETE to confirm:`;
    const input = window.prompt(warning);
    if (input?.trim().toUpperCase() !== 'DELETE') return;
    setIsDeleting(true);
    try {
      const wa = walletAddress ? `?walletAddress=${encodeURIComponent(walletAddress)}` : '';
      const res = await fetch(`/api/admin/geofence/proposals/${proposal.id}${wa}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
      setNotification({ type: 'success', msg: 'Proposal deleted.' });
      onActionDone();
    } catch (e: any) { setNotification({ type: 'error', msg: e.message }); }
    finally { setIsDeleting(false); }
  };

  const hasGeometry = !!(proposal.originalFeatureSnapshot || proposal.proposedFeatureSnapshot);

  const actionBtn = (label: string, onClick: () => void, busy: boolean, color: string, disabled = false): React.ReactNode => (
    <button onClick={onClick} disabled={busy || disabled} style={{
      padding: '4px 12px', fontSize: 11, fontWeight: 600, border: 'none', borderRadius: 4, cursor: busy || disabled ? 'not-allowed' : 'pointer',
      backgroundColor: busy || disabled ? '#e5e7eb' : color, color: busy || disabled ? '#9ca3af' : '#fff',
    }}>{label}</button>
  );

  return (
    <div style={{ padding: '12px 16px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
      {notification && (
        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, fontSize: 12,
          backgroundColor: notification.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: notification.type === 'success' ? '#065f46' : '#991b1b' }}>
          {notification.msg}
          <button onClick={() => setNotification(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'inherit' }}>✕</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '12px', fontSize: '12px' }}>
        {/* Geometry diff */}
        <div>
          <div style={{ fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' as const, fontSize: 10, letterSpacing: '0.05em' }}>Geometry Change</div>
          {diff ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, color: '#374151' }}>
              {diff.vertices && (
                <div>Vertices: {diff.vertices.original} → {diff.vertices.proposed}
                  <span style={{ marginLeft: 4, color: diff.vertices.delta > 0 ? '#16a34a' : diff.vertices.delta < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                    ({diff.vertices.delta > 0 ? '+' : ''}{diff.vertices.delta})
                  </span>
                  {diff.vertices.changedPositions != null && diff.vertices.changedPositions > 0 && diff.vertices.delta === 0 && (
                    <span style={{ marginLeft: 6, color: '#92400e', fontSize: 11 }}>
                      · {diff.vertices.changedPositions} position{diff.vertices.changedPositions !== 1 ? 's' : ''} have different coordinate values
                    </span>
                  )}
                </div>
              )}
              {diff.area?.approxOriginalKm2 !== undefined && (
                <div>Area: {diff.area.approxOriginalKm2.toLocaleString()} → {diff.area.approxProposedKm2.toLocaleString()} km²
                  {diff.area.deltaPct !== null && (
                    <span style={{ marginLeft: 4, color: (diff.area.deltaPct || 0) > 0 ? '#16a34a' : (diff.area.deltaPct || 0) < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                      ({(diff.area.deltaPct || 0) > 0 ? '+' : ''}{diff.area.deltaPct}%)
                    </span>
                  )}
                </div>
              )}
              {diff.centroid && <div>Centroid shift: ~{diff.centroid.approxShiftKm} km</div>}
              {diff.geometryTypeChanged && <div style={{ color: '#b45309' }}>⚠ Geometry type changed</div>}
              {/* Precision-only hint: no vertices added/removed, no measurable area change, but some coordinates differ */}
              {diff.vertices?.delta === 0 &&
               diff.vertices?.changedPositions != null &&
               diff.vertices.changedPositions > 0 &&
               (diff.area?.deltaPct === 0 || diff.area?.deltaPct === null) && (
                <div style={{ marginTop: 4, padding: '4px 8px', background: '#fef3c7', borderRadius: 4, fontSize: 11, color: '#92400e' }}>
                  ℹ Coordinate precision normalization — no measurable geometric change. {diff.vertices.changedPositions} of {diff.vertices.original} positions were rounded; map display and area are unchanged.
                </div>
              )}
              {diff.vertices?.delta === 0 &&
               diff.vertices?.changedPositions === 0 && (
                <div style={{ marginTop: 4, padding: '4px 8px', background: '#f1f5f9', borderRadius: 4, fontSize: 11, color: '#64748b' }}>
                  ℹ Proposed geometry is identical to the original — no coordinate values changed.
                </div>
              )}
            </div>
          ) : <div style={{ color: '#94a3b8' }}>Loading diff…</div>}
        </div>

        {/* Details */}
        <div>
          <div style={{ fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' as const, fontSize: 10, letterSpacing: '0.05em' }}>Details</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, color: '#374151' }}>
            <div>Submitted: {fmtDate(proposal.createdAt)}</div>
            {proposal.approvedAt && <div>Approved: {fmtDate(proposal.approvedAt)}</div>}
            {proposal.rejectedAt && <div>Rejected: {fmtDate(proposal.rejectedAt)}</div>}
            {proposal.approvedBy && <div>By: {truncAddr(proposal.approvedBy)}</div>}
            {proposal.rejectionReason && <div style={{ color: '#dc2626' }}>Reason: {proposal.rejectionReason}</div>}
          </div>
        </div>

        {/* Reward */}
        <div>
          <div style={{ fontWeight: 700, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' as const, fontSize: 10, letterSpacing: '0.05em' }}>Reward</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, color: '#374151', fontSize: 12 }}>
            {proposal.rewardAmount
              ? <div>{proposal.rewardAmount} {proposal.rewardCurrency || 'USDC'}</div>
              : <div style={{ color: '#94a3b8' }}>No reward recorded</div>}
            {proposal.rewardTxHash && <div>Tx: <TxLink hash={proposal.rewardTxHash} /></div>}
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, alignItems: 'center', marginBottom: isApprovedOrBeyond ? '12px' : 0 }}>
        {hasGeometry && (
          <button onClick={onCompare}
            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, backgroundColor: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer' }}>
            🗺 Compare Borders
          </button>
        )}
        {proposal.status === 'submitted' && (
          <>
            <button onClick={handleApprove} disabled={isApproving || isRejecting}
              style={{ padding: '6px 16px', fontSize: 12, fontWeight: 700, backgroundColor: isApproving ? '#e5e7eb' : '#16a34a', color: isApproving ? '#9ca3af' : '#fff', border: 'none', borderRadius: 6, cursor: isApproving ? 'not-allowed' : 'pointer' }}>
              {isApproving ? 'Approving…' : '✓ Approve'}
            </button>
            <button onClick={handleReject} disabled={isApproving || isRejecting}
              style={{ padding: '6px 16px', fontSize: 12, fontWeight: 700, backgroundColor: isRejecting ? '#e5e7eb' : '#dc2626', color: isRejecting ? '#9ca3af' : '#fff', border: 'none', borderRadius: 6, cursor: isRejecting ? 'not-allowed' : 'pointer' }}>
              {isRejecting ? 'Rejecting…' : '✕ Reject'}
            </button>
          </>
        )}
        {/* Delete — only available when NOT anchored on-chain */}
        {!proposal.onChainAnchoredAt && (
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            title="Permanently delete this proposal record. Cannot delete once anchored on-chain."
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: isDeleting ? 'not-allowed' : 'pointer',
              backgroundColor: isDeleting ? '#e5e7eb' : 'transparent',
              color: isDeleting ? '#9ca3af' : '#dc2626',
              border: '1px solid #fca5a5',
              marginLeft: 'auto',
            }}>
            {isDeleting ? 'Deleting…' : '🗑 Delete'}
          </button>
        )}
      </div>

      {/* ── On-Chain Rewards Panel (approved+) ──────────────────────────────── */}
      {isApprovedOrBeyond && (
        <div style={{ marginTop: 4, padding: '10px 12px', backgroundColor: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd', fontSize: 12 }}>
          {/* Panel header */}
          <div style={{ fontWeight: 700, color: '#0369a1', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
            <span>◆ On-Chain Rewards</span>
            <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 10 }}>{STELLAR_NETWORK}</span>
            {!onChainChecked && <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 11 }}>checking…</span>}
            {REWARDS_CONTRACT_ID && (
              <ContractLink id={REWARDS_CONTRACT_ID} label="View contract ↗" />
            )}
          </div>

          {/* Receipt section */}
          {onChainChecked && (
            onChainReceipt ? (
              <div style={{ marginBottom: 8, color: '#374151' }}>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ Anchored</span>
                {' · '}Status: <strong>{String(onChainReceipt.status ?? '').replace('_', ' ')}</strong>
                {proposal.onChainSubmitHash && (
                  <div style={{ fontSize: 11, marginTop: 2 }}>
                    Submit tx: <TxLink hash={proposal.onChainSubmitHash} />
                  </div>
                )}
                {proposal.onChainApproveHash && (
                  <div style={{ fontSize: 11 }}>
                    Approve tx: <TxLink hash={proposal.onChainApproveHash} />
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: 8, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
                Not anchored on-chain yet.
                {actionBtn(isAnchoring ? 'Anchoring…' : '⚓ Anchor on-chain', handleAnchor, isAnchoring, '#0369a1')}
              </div>
            )
          )}

          {/* Escrow section — only once receipt exists */}
          {onChainReceipt && onChainChecked && (
            <div style={{ borderTop: '1px solid #bae6fd', paddingTop: 8, marginTop: 4 }}>
              <div style={{ fontWeight: 600, color: '#0369a1', marginBottom: 6 }}>Reward Escrow</div>

              {onChainEscrow && !onChainEscrow.released && !onChainEscrow.refunded ? (
                /* Funded, waiting for settlement */
                <div>
                  <div style={{ color: '#555', marginBottom: 6 }}>
                    <span style={{ color: '#16a34a' }}>● Funded</span>
                    {' · Amount: '}<strong>{onChainEscrow.amount}</strong>
                    {proposal.onChainFundHash && (
                      <span style={{ marginLeft: 6, fontSize: 11 }}>
                        Fund tx: <TxLink hash={proposal.onChainFundHash} />
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {actionBtn(isReleasing ? 'Releasing…' : '▶ Release to Agent', handleRelease, isReleasing, '#16a34a', isRefunding)}
                    {actionBtn(isRefunding ? 'Refunding…' : '↩ Refund to Treasury', handleRefund, isRefunding, '#dc2626', isReleasing)}
                  </div>
                </div>
              ) : onChainEscrow?.released ? (
                <div style={{ color: '#16a34a' }}>
                  ✓ Reward released to agent.
                  {proposal.onChainReleaseHash && (
                    <span style={{ marginLeft: 6, fontSize: 11 }}>
                      Release tx: <TxLink hash={proposal.onChainReleaseHash} />
                    </span>
                  )}
                </div>
              ) : onChainEscrow?.refunded ? (
                <div style={{ color: '#6b7280' }}>↩ Escrow refunded to treasury.</div>
              ) : (
                /* No escrow yet — fund form */
                <div>
                  <div style={{ color: '#6b7280', marginBottom: 8 }}>No reward escrow yet. Select a token and enter the amount to pay the agent on-chain.</div>

                  {/* Token quick-select */}
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 11, color: '#374151', marginBottom: 4, fontWeight: 600 }}>Token</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 6 }}>
                      {TOKEN_PRESETS.map(t => (
                        <button key={t.symbol}
                          onClick={() => setFundTokenAddr(t.addr)}
                          style={{
                            padding: '3px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer', fontWeight: 600,
                            border: fundTokenAddr === t.addr ? '2px solid #0369a1' : '1px solid #bae6fd',
                            backgroundColor: fundTokenAddr === t.addr ? '#e0f2fe' : '#fff',
                            color: fundTokenAddr === t.addr ? '#0369a1' : '#374151',
                          }}>
                          {t.symbol}
                        </button>
                      ))}
                      <button
                        onClick={() => setFundTokenAddr('')}
                        style={{
                          padding: '3px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                          border: !TOKEN_PRESETS.some(t => t.addr === fundTokenAddr) && fundTokenAddr ? '2px solid #0369a1' : '1px solid #d1d5db',
                          backgroundColor: !TOKEN_PRESETS.some(t => t.addr === fundTokenAddr) && fundTokenAddr ? '#e0f2fe' : '#f9fafb',
                          color: '#6b7280',
                        }}>
                        Custom
                      </button>
                    </div>
                    <input type="text" value={fundTokenAddr} onChange={e => setFundTokenAddr(e.target.value)}
                      placeholder="C… Soroban token contract address"
                      style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #bae6fd', fontFamily: 'monospace', boxSizing: 'border-box' as const, width: '100%', maxWidth: 420 }}
                    />
                    {TOKEN_PRESETS.find(t => t.addr === fundTokenAddr) && (
                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                        {STELLAR_NETWORK} · Verify on{' '}
                        <a href={explorerContract(fundTokenAddr)!} target="_blank" rel="noopener noreferrer" style={{ color: '#0369a1' }}>
                          Stellar Expert ↗
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Amount */}
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: '#374151', marginBottom: 4, fontWeight: 600 }}>
                      Amount{' '}
                      <span style={{ fontWeight: 400, color: '#64748b' }}>
                        ({TOKEN_PRESETS.find(t => t.addr === fundTokenAddr)?.unit ?? '7 decimal places — 10,000,000 = 1 token'})
                      </span>
                    </div>
                    <input type="number" value={fundAmountStr} onChange={e => setFundAmountStr(e.target.value)}
                      placeholder="10000000" min="1"
                      style={{ padding: '4px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #bae6fd', width: 160 }}
                    />
                  </div>

                  {actionBtn(isFunding ? 'Funding…' : '💰 Fund Escrow', handleFund, isFunding, '#0369a1', !fundTokenAddr.trim() || !fundAmountStr.trim())}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Mobile proposal card ─────────────────────────────────────────────────────

function ProposalCard({ p, expanded, onToggle, onCompare, walletAddress, onActionDone, submissionCounts }: {
  p: Proposal; expanded: boolean;
  onToggle: () => void; onCompare: () => void;
  walletAddress: string | null; onActionDone: () => void;
  submissionCounts: Record<string, number>;
}) {
  return (
    <div style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: expanded ? '#f0f9ff' : '#fff' }}>
      <div
        onClick={onToggle}
        style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 4, fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>
              {p.countryCode}
            </span>
            <StatusBadge status={p.status} />
            {p.onChainAnchoredAt && REWARDS_CONTRACT_ID && (
              <a href={explorerContract(REWARDS_CONTRACT_ID)!} target="_blank" rel="noopener noreferrer"
                onClick={e => e.stopPropagation()} title={`Anchored on-chain (${STELLAR_NETWORK})`}
                style={{ color: '#16a34a', textDecoration: 'none', fontSize: 13 }}>◆</a>
            )}
          </div>
          <span style={{ fontSize: 11, color: '#64748b' }}>{expanded ? '▲' : '▼'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', alignItems: 'center', gap: 8 }}>
          <span>
            {truncAddr(p.submittedBy)}
            {submissionCounts[submitterCountKey(p.submittedBy)] != null && (
              <span
                title="Total proposals with this submitter (current filters)"
                style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#0369a1', backgroundColor: '#e0f2fe', padding: '1px 6px', borderRadius: 4 }}
              >
                ×{submissionCounts[submitterCountKey(p.submittedBy)]}
              </span>
            )}
          </span>
          <span>{fmtDate(p.createdAt)}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {(p.originalFeatureSnapshot || p.proposedFeatureSnapshot) && (
            <button
              onClick={e => { e.stopPropagation(); onCompare(); }}
              style={{ padding: '5px 12px', fontSize: 11, fontWeight: 700, backgroundColor: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 6, cursor: 'pointer' }}>
              🗺 Compare
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <ProposalDetailPanel
          proposal={p} walletAddress={walletAddress}
          onActionDone={onActionDone} onCompare={onCompare}
        />
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const BorderProposalsDashboard: React.FC<BorderProposalsDashboardProps> = ({
  isOpen, onClose, walletAddress, mainAdminAddress,
  accountBalance = null,
  passkeyAccountAddress = null,
  onRefreshWalletBalances,
}) => {
  const isMobile = useIsMobile();
  const isMainAdmin = !!(walletAddress && mainAdminAddress && walletAddress === mainAdminAddress);

  const [proposals, setProposals]           = useState<Proposal[]>([]);
  const [total, setTotal]                   = useState(0);
  const [isLoading, setIsLoading]           = useState(false);
  const [error, setError]                   = useState<string | null>(null);
  const [expandedId, setExpandedId]         = useState<string | null>(null);
  const [compareProposal, setCompareProposal] = useState<Proposal | null>(null);

  const [filterStatus, setFilterStatus]               = useState('');
  const [filterCountry, setFilterCountry]             = useState('');
  const [filterCountryInput, setFilterCountryInput]   = useState('');
  const [offset, setOffset]                           = useState(0);
  const [submissionCounts, setSubmissionCounts]       = useState<Record<string, number>>({});
  const [adminTxs, setAdminTxs]                         = useState<HorizonTxRecord[]>([]);
  const [txOpByHash, setTxOpByHash]                     = useState<Record<string, string[]>>({});
  const [txNextHref, setTxNextHref]                   = useState<string | null>(null);
  const [txPrevHref, setTxPrevHref]                   = useState<string | null>(null);
  const [txPageError, setTxPageError]                 = useState<string | null>(null);
  const [copiedHash, setCopiedHash]                   = useState<string | null>(null);
  const [txLoading, setTxLoading]                       = useState(false);
  const [passkeyXlm, setPasskeyXlm]                     = useState<string | null>(null);
  const [activeTab, setActiveTab]                       = useState<'proposals' | 'transactions'>('proposals');
  const PAGE = 25;
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchProposals = useCallback(async (opts: { status?: string; country?: string; off?: number } = {}) => {
    if (!walletAddress) return;
    setIsLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set('walletAddress', walletAddress);
      params.set('limit', String(PAGE));
      params.set('offset', String(opts.off ?? 0));
      if (opts.status)  params.set('status', opts.status);
      if (opts.country) params.set('countryCode', opts.country.toUpperCase().trim());
      const res = await fetch(`/api/admin/geofence/proposals?${params}`);
      if (res.status === 401 || res.status === 403) { setError('Access denied. You need admin or country admin permissions.'); return; }
      if (res.status === 400) { const d = await res.json(); setError(d.error || 'Country code required for country admins.'); return; }
      if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.error || `Server error ${res.status}`); return; }
      const data = await res.json();
      setProposals(data.proposals || []);
      setTotal(data.total ?? data.count ?? 0);
      if (data.submissionCounts && typeof data.submissionCounts === 'object') {
        setSubmissionCounts(data.submissionCounts);
      } else {
        setSubmissionCounts({});
      }
    } catch (e: any) { setError(e.message || 'Network error'); }
    finally { setIsLoading(false); }
  }, [walletAddress]);

  useEffect(() => {
    if (!isOpen || !walletAddress) return;
    setOffset(0);
    fetchProposals({ status: filterStatus || undefined, country: filterCountry || undefined, off: 0 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, walletAddress]);

  useEffect(() => {
    if (!isOpen) return;
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(() => {
      setOffset(0);
      fetchProposals({ status: filterStatus || undefined, country: filterCountry || undefined, off: 0 });
    }, 400);
    return () => { if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterCountry]);

  /** Toolbar refresh — keeps expanded row open */
  const doRefresh = useCallback(() => {
    fetchProposals({ status: filterStatus || undefined, country: filterCountry || undefined, off: offset });
  }, [fetchProposals, filterStatus, filterCountry, offset]);

  /** After approve / reject / fund / release / … — collapse detail + compare + reload */
  const handleProposalActionDone = useCallback(() => {
    setExpandedId(null);
    setCompareProposal(null);
    fetchProposals({ status: filterStatus || undefined, country: filterCountry || undefined, off: offset });
    onRefreshWalletBalances?.();
  }, [fetchProposals, filterStatus, filterCountry, offset, onRefreshWalletBalances]);

  const loadAdminTransactionsPage = useCallback(async (pageUrl: string | null) => {
    if (!walletAddress) return;
    setTxLoading(true);
    setTxPageError(null);
    const url =
      pageUrl ||
      `${HORIZON_BASE}/accounts/${encodeURIComponent(walletAddress)}/transactions?limit=${TX_PAGE_LIMIT}&order=desc`;
    try {
      const r = await fetch(url);
      if (!r.ok) {
        setAdminTxs([]);
        setTxOpByHash({});
        setTxNextHref(null);
        setTxPrevHref(null);
        setTxPageError(`Horizon returned ${r.status}`);
        return;
      }
      const d = await r.json();
      const records: HorizonTxRecord[] = d._embedded?.records || [];
      setAdminTxs(records);
      setTxNextHref(typeof d._links?.next?.href === 'string' ? d._links.next.href : null);
      setTxPrevHref(typeof d._links?.prev?.href === 'string' ? d._links.prev.href : null);

      const entries = await Promise.all(
        records.map(async (tx) => {
          const h = tx.hash;
          if (!h) return [h, []] as const;
          try {
            const opR = await fetch(
              `${HORIZON_BASE}/transactions/${encodeURIComponent(h)}/operations?limit=200&order=asc`,
            );
            const opD = opR.ok ? await opR.json() : { _embedded: { records: [] } };
            const lines = (opD._embedded?.records || []).map((op: any) =>
              formatHorizonOperation(op, walletAddress),
            );
            return [h, lines] as const;
          } catch {
            return [h, []] as const;
          }
        }),
      );
      const nextOps: Record<string, string[]> = {};
      for (const [h, lines] of entries) {
        if (h) nextOps[h] = lines;
      }
      setTxOpByHash(nextOps);
    } catch (e: any) {
      setAdminTxs([]);
      setTxOpByHash({});
      setTxNextHref(null);
      setTxPrevHref(null);
      setTxPageError(e?.message || 'Network error');
    } finally {
      setTxLoading(false);
    }
  }, [walletAddress]);

  const refreshAdminTransactions = useCallback(() => {
    loadAdminTransactionsPage(null);
  }, [loadAdminTransactionsPage]);

  const copyTxHash = useCallback((h: string) => {
    if (!h) return;
    void navigator.clipboard?.writeText(h).then(
      () => {
        setCopiedHash(h);
        window.setTimeout(() => {
          setCopiedHash((c) => (c === h ? null : c));
        }, 2000);
      },
      () => {},
    );
  }, []);

  const loadPasskeyBalance = useCallback(async () => {
    if (!passkeyAccountAddress) {
      setPasskeyXlm(null);
      return;
    }
    try {
      const r = await fetch('/api/passkey-kit/account-balance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountAddress: passkeyAccountAddress }),
      });
      const d = await r.json();
      if (d.success && typeof d.balance === 'number') {
        setPasskeyXlm((d.balance / 10_000_000).toFixed(7));
      } else {
        setPasskeyXlm(null);
      }
    } catch {
      setPasskeyXlm(null);
    }
  }, [passkeyAccountAddress]);

  useEffect(() => {
    if (!isOpen || !walletAddress) return;
    onRefreshWalletBalances?.();
    loadPasskeyBalance();
  }, [isOpen, walletAddress, onRefreshWalletBalances, loadPasskeyBalance]);

  useEffect(() => {
    if (!isOpen) setActiveTab('proposals');
  }, [isOpen]);

  useEffect(() => {
    if (!walletAddress) {
      setAdminTxs([]);
      setTxOpByHash({});
      setTxNextHref(null);
      setTxPrevHref(null);
      setTxPageError(null);
    }
  }, [walletAddress]);

  useEffect(() => {
    if (!isOpen || !walletAddress || activeTab !== 'transactions') return;
    loadAdminTransactionsPage(null);
  }, [isOpen, walletAddress, activeTab, loadAdminTransactionsPage]);

  if (!isOpen) return null;

  const totalPages   = Math.ceil(total / PAGE);
  const currentPage  = Math.floor(offset / PAGE) + 1;

  return (
    <>
      {compareProposal && (
        <BorderCompareModal proposal={compareProposal} onClose={() => setCompareProposal(null)} />
      )}

      <div
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div style={{
          backgroundColor: '#fff',
          borderRadius: isMobile ? '16px 16px 0 0' : '12px',
          width: isMobile ? '100%' : 'min(1100px, 96vw)',
          maxHeight: isMobile ? '92vh' : '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}>

          {/* Header */}
          <div style={{
            padding: isMobile ? '14px 16px 10px' : '18px 24px 14px',
            borderBottom: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            color: '#fff', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px', fontWeight: 700 }}>Border Proposals</h2>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {isMainAdmin ? 'All countries' : 'Filter by your country code below'}
                  {total > 0 && ` · ${total} proposal${total !== 1 ? 's' : ''}`}
                </div>
                {!isMobile && <ContractLinksBar />}
              </div>
              <button onClick={onClose}
                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 24, cursor: 'pointer', lineHeight: 1, padding: '4px 4px 4px 12px' }}>
                ✕
              </button>
            </div>
            {isMobile && <ContractLinksBar />}
          </div>

          {/* Proposals vs transaction history */}
          <div
            role="tablist"
            aria-label="Border proposals sections"
            style={{
              display: 'flex',
              gap: isMobile ? 8 : 10,
              padding: isMobile ? '10px 12px' : '10px 24px',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#f1f5f9',
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'proposals'}
              onClick={() => setActiveTab('proposals')}
              style={{
                flex: isMobile ? 1 : undefined,
                minHeight: 44,
                padding: '10px 14px',
                fontSize: isMobile ? 13 : 13,
                fontWeight: 700,
                borderRadius: 8,
                cursor: 'pointer',
                border: activeTab === 'proposals' ? '1px solid #cbd5e1' : '1px solid transparent',
                backgroundColor: activeTab === 'proposals' ? '#fff' : 'transparent',
                color: activeTab === 'proposals' ? '#0f172a' : '#64748b',
                boxShadow: activeTab === 'proposals' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              Proposals
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'transactions'}
              onClick={() => setActiveTab('transactions')}
              style={{
                flex: isMobile ? 1 : undefined,
                minHeight: 44,
                padding: '10px 14px',
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 8,
                cursor: 'pointer',
                border: activeTab === 'transactions' ? '1px solid #cbd5e1' : '1px solid transparent',
                backgroundColor: activeTab === 'transactions' ? '#fff' : 'transparent',
                color: activeTab === 'transactions' ? '#0f172a' : '#64748b',
                boxShadow: activeTab === 'transactions' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              Transactions
            </button>
          </div>

          {activeTab === 'proposals' && (
          <div style={{
            padding: isMobile ? '10px 14px' : '12px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row' as const,
            gap: isMobile ? '8px' : '12px',
            alignItems: isMobile ? 'stretch' : 'center',
            flexWrap: 'wrap' as const,
            backgroundColor: '#f8fafc', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' as const }}>Status</label>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  style={{ fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff' }}>
                  <option value=''>All</option>
                  <option value='submitted'>Submitted</option>
                  <option value='approved'>Approved</option>
                  <option value='rejected'>Rejected</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' as const }}>
                  Country {!isMainAdmin && <span style={{ color: '#dc2626' }}>*</span>}
                </label>
                <input type='text' value={filterCountryInput}
                  onChange={e => setFilterCountryInput(e.target.value.toUpperCase())}
                  onKeyDown={e => { if (e.key === 'Enter') setFilterCountry(filterCountryInput.toUpperCase().trim()); }}
                  placeholder='US' maxLength={3}
                  style={{ width: 50, fontSize: 12, padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontFamily: 'monospace' }}
                />
                <button onClick={() => setFilterCountry(filterCountryInput.toUpperCase().trim())}
                  style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: 'none', backgroundColor: '#0369a1', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                  Filter
                </button>
                {filterCountry && (
                  <button onClick={() => { setFilterCountry(''); setFilterCountryInput(''); }}
                    style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#64748b', cursor: 'pointer' }}>
                    Clear
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={() => { doRefresh(); loadPasskeyBalance(); onRefreshWalletBalances?.(); }}
              disabled={isLoading}
              style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: isLoading ? '#f1f5f9' : '#fff', color: '#374151', cursor: isLoading ? 'not-allowed' : 'pointer', marginLeft: isMobile ? 0 : 'auto' }}>
              {isLoading ? 'Loading…' : '↺ Refresh'}
            </button>
          </div>
          )}

          {walletAddress && (
            <div style={{
              padding: isMobile ? '12px 14px' : '12px 24px',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#fafafa',
              fontSize: 12,
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap' as const, alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <span style={{ fontWeight: 700, color: '#334155' }}>Wallet</span>
                <button
                  type="button"
                  onClick={() => { loadPasskeyBalance(); onRefreshWalletBalances?.(); }}
                  style={{ fontSize: 11, padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: '#fff', cursor: 'pointer', color: '#475569', minHeight: 36 }}
                >
                  Refresh balances
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '16px 28px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Classic wallet</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#0f172a', marginTop: 2, wordBreak: 'break-all' as const }} title={walletAddress}>
                    {walletAddress.slice(0, 10)}…{walletAddress.slice(-6)}
                  </div>
                  <div style={{ marginTop: 4, color: '#374151' }}>
                    XLM: <strong>{accountBalance ?? '—'}</strong>
                  </div>
                </div>
                {passkeyAccountAddress ? (
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Passkey smart account</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#0f172a', marginTop: 2, wordBreak: 'break-all' as const }} title={passkeyAccountAddress}>
                      {passkeyAccountAddress.slice(0, 10)}…{passkeyAccountAddress.slice(-6)}
                    </div>
                    <div style={{ marginTop: 4, color: '#374151' }}>
                      XLM: <strong>{passkeyXlm ?? '…'}</strong>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#94a3b8', fontSize: 11, alignSelf: 'center' }}>No passkey smart account linked</div>
                )}
              </div>
            </div>
          )}

          {/* Body — proposals: single scroll; transactions: list scroll + fixed pager */}
          {activeTab === 'proposals' ? (
          <div style={{ flex: 1, overflowY: 'auto' as const, minHeight: 0 }}>
            {error ? (
              <div style={{ padding: '40px 24px', textAlign: 'center' as const }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⚠</div>
                <div style={{ color: '#dc2626', fontWeight: 600, marginBottom: 6 }}>{error}</div>
                {!isMainAdmin && !filterCountry && (
                  <div style={{ color: '#64748b', fontSize: 13 }}>Enter your country code in the filter above.</div>
                )}
              </div>
            ) : proposals.length === 0 && !isLoading ? (
              <div style={{ padding: '60px 24px', textAlign: 'center' as const, color: '#94a3b8' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>No proposals found</div>
                <div style={{ fontSize: 13 }}>
                  {!isMainAdmin && !filterCountry ? 'Enter your country code above.' : 'No proposals match the current filters.'}
                </div>
              </div>
            ) : isMobile ? (
              /* ── Mobile card layout ── */
              <div>
                {proposals.map(p => (
                  <ProposalCard
                    key={p.id} p={p}
                    expanded={expandedId === p.id}
                    onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                    onCompare={() => setCompareProposal(p)}
                    walletAddress={walletAddress}
                    onActionDone={handleProposalActionDone}
                    submissionCounts={submissionCounts}
                  />
                ))}
              </div>
            ) : (
              /* ── Desktop table layout ── */
              <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    {['Country', 'Status', 'Submitted By', 'Subs', 'Vertex Δ', 'Date', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left' as const, fontWeight: 700, color: '#64748b', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: '0.05em', whiteSpace: 'nowrap' as const }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {proposals.map(p => (
                    <React.Fragment key={p.id}>
                      <tr
                        style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: expandedId === p.id ? '#f0f9ff' : '#fff', transition: 'background 0.1s' }}
                        onMouseEnter={e => { if (expandedId !== p.id) (e.currentTarget as HTMLElement).style.backgroundColor = '#f8fafc'; }}
                        onMouseLeave={e => { if (expandedId !== p.id) (e.currentTarget as HTMLElement).style.backgroundColor = '#fff'; }}
                      >
                        <td style={{ padding: '10px 16px', fontWeight: 700, fontFamily: 'monospace' }}>
                          <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 4 }}>{p.countryCode}</span>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <StatusBadge status={p.status} />
                            {p.onChainAnchoredAt && REWARDS_CONTRACT_ID && (
                              <a href={explorerContract(REWARDS_CONTRACT_ID)!} target="_blank" rel="noopener noreferrer"
                                title={`Anchored on-chain (${STELLAR_NETWORK})`}
                                style={{ color: '#16a34a', textDecoration: 'none', fontSize: 13 }}>◆</a>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '10px 16px', color: '#64748b', fontFamily: 'monospace', fontSize: 12 }}>
                          <span title={p.submittedBy || 'Unknown'}>{truncAddr(p.submittedBy)}</span>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' as const }}>
                          {submissionCounts[submitterCountKey(p.submittedBy)] != null ? (
                            <span
                              title="Proposals with this submitter under current filters"
                              style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', backgroundColor: '#e0f2fe', padding: '2px 8px', borderRadius: 9999 }}
                            >
                              {submissionCounts[submitterCountKey(p.submittedBy)]}
                            </span>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>{vertexDelta(p)}</td>
                        <td style={{ padding: '10px 16px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' as const }}>{fmtDate(p.createdAt)}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
                            <button
                              onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                              style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4, border: '1px solid #cbd5e1', backgroundColor: expandedId === p.id ? '#e0f2fe' : '#fff', color: expandedId === p.id ? '#0369a1' : '#374151', cursor: 'pointer', fontWeight: 600 }}>
                              {expandedId === p.id ? '▲ Hide' : '▼ Details'}
                            </button>
                            {(p.originalFeatureSnapshot || p.proposedFeatureSnapshot) && (
                              <button onClick={() => setCompareProposal(p)}
                                style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4, border: '1px solid #c7d2fe', backgroundColor: '#eef2ff', color: '#4338ca', cursor: 'pointer', fontWeight: 600 }}>
                                🗺 Compare
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedId === p.id && (
                        <tr key={`${p.id}-detail`}>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <ProposalDetailPanel proposal={p} walletAddress={walletAddress} onActionDone={handleProposalActionDone} onCompare={() => setCompareProposal(p)} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          ) : (
          <div
            role="tabpanel"
            aria-label="Transaction history"
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                padding: isMobile ? '12px 14px' : '14px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: isMobile ? 'column' as const : 'row' as const,
                alignItems: isMobile ? 'stretch' : 'center',
                justifyContent: 'space-between',
                gap: 12,
                backgroundColor: '#fafafa',
              }}
            >
              <div>
                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: isMobile ? 15 : 16 }}>Account transactions</span>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  {TX_PAGE_LIMIT} per page · from Horizon ({STELLAR_NETWORK})
                </div>
              </div>
              <button
                type="button"
                onClick={() => refreshAdminTransactions()}
                disabled={txLoading || !walletAddress}
                style={{
                  minHeight: 44,
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  backgroundColor: txLoading || !walletAddress ? '#f1f5f9' : '#fff',
                  cursor: txLoading || !walletAddress ? 'not-allowed' : 'pointer',
                  color: '#374151',
                }}
              >
                {txLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' as const, minHeight: 0, padding: isMobile ? '14px 14px' : '18px 24px' }}>
              {!walletAddress ? (
                <p style={{ color: '#64748b', fontSize: 14, margin: 0 }}>Connect a wallet to load transactions.</p>
              ) : txPageError ? (
                <div style={{ color: '#b91c1c', fontSize: 14, lineHeight: 1.5 }}>{txPageError}</div>
              ) : txLoading && adminTxs.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 14 }}>Loading…</div>
              ) : adminTxs.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>No transactions yet, or Horizon did not return data.</div>
              ) : (
                <div>
                  {adminTxs.map((tx) => (
                    <TransactionHistoryCard
                      key={tx.id || tx.hash || String(tx.created_at)}
                      tx={tx}
                      viewerG={walletAddress}
                      opLines={tx.hash ? txOpByHash[tx.hash] ?? [] : []}
                      isMobile={isMobile}
                      onCopyHash={copyTxHash}
                      copiedHash={copiedHash}
                    />
                  ))}
                </div>
              )}
            </div>

            {walletAddress && (
              <div
                style={{
                  flexShrink: 0,
                  padding: isMobile ? '12px 14px max(16px, env(safe-area-inset-bottom))' : '12px 24px',
                  borderTop: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap' as const,
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {adminTxs.length > 0 ? `${adminTxs.length} on this page` : ' '}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    disabled={txLoading || !txPrevHref}
                    onClick={() => txPrevHref && loadAdminTransactionsPage(txPrevHref)}
                    title="More recent transactions"
                    style={{
                      minHeight: 40,
                      padding: '8px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      backgroundColor: !txPrevHref || txLoading ? '#f1f5f9' : '#fff',
                      color: !txPrevHref || txLoading ? '#9ca3af' : '#374151',
                      cursor: !txPrevHref || txLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    ← Newer
                  </button>
                  <button
                    type="button"
                    disabled={txLoading || !txNextHref}
                    onClick={() => txNextHref && loadAdminTransactionsPage(txNextHref)}
                    title="Older transactions"
                    style={{
                      minHeight: 40,
                      padding: '8px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      backgroundColor: !txNextHref || txLoading ? '#f1f5f9' : '#fff',
                      color: !txNextHref || txLoading ? '#9ca3af' : '#374151',
                      cursor: !txNextHref || txLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Older →
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Pagination — proposals tab only */}
          {activeTab === 'proposals' && total > 0 && (
            <div style={{ padding: '12px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: 8, backgroundColor: '#f8fafc', flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Showing <strong>{offset + 1}</strong>–<strong>{offset + proposals.length}</strong> of <strong>{total}</strong>
                {' · '}
                Page {currentPage} / {Math.max(1, totalPages)}
                {' · '}
                {PAGE} per page
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button disabled={offset === 0 || isLoading} onClick={() => { const o = Math.max(0, offset - PAGE); setOffset(o); fetchProposals({ status: filterStatus || undefined, country: filterCountry || undefined, off: o }); }}
                  style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: offset === 0 ? '#f1f5f9' : '#fff', color: offset === 0 ? '#9ca3af' : '#374151', cursor: offset === 0 || isLoading ? 'not-allowed' : 'pointer' }}>
                  ← Prev
                </button>
                <button disabled={offset + PAGE >= total || isLoading} onClick={() => { const o = offset + PAGE; setOffset(o); fetchProposals({ status: filterStatus || undefined, country: filterCountry || undefined, off: o }); }}
                  style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid #cbd5e1', backgroundColor: offset + PAGE >= total ? '#f1f5f9' : '#fff', color: offset + PAGE >= total ? '#9ca3af' : '#374151', cursor: offset + PAGE >= total || isLoading ? 'not-allowed' : 'pointer' }}>
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
